import { bool, number, oneOf, func } from 'prop-types';
import styled, { css, keyframes } from 'styled-components/macro';
import { rgba, hideVisually } from 'polished';
import TippyToolTip from 'components/base/TippyToolTip/TippyToolTip';

import modularScale from 'styles/helpers/modular-scale';

import { ReactComponent as IconTryAgain } from 'icons/icon-try-again.svg';
import { ReactComponent as IconUnfollow } from 'icons/icon-unfollow.svg';
import { ReactComponent as IconCheckmark } from 'icons/icon-checkmark.svg';

const toggleIsNotFollowing = ({ isDisabled }) => css`
  background-color: white;
  color: ${({ theme }) => theme.NightBlue};
  border: 1px solid transparent;
  font-weight: 600;
  min-width: 9ch;

  &:hover {
    background-color: ${({ theme }) => theme.Sand};
  }

  &:active {
    ${({ theme }) => !isDisabled && `background-color: ${theme.LightGrey};`};
  }
`;

export const animateCheckmark = keyframes`
  0%, 33%{
    stroke-dashoffset: 100;
  }
  100%{
    stroke-dashoffset: 0;
  }
`;

const toggleIsFollowing = () => css`
  background-color: none;
  color: white;
  border: 1px solid;
  min-width: 14ch;

  ${({ isHovered, hasError }) =>
    isHovered || hasError
      ? css`
          svg:nth-of-type(2) {
            ${hideVisually};
          }
        `
      : css`
          svg:first-of-type {
            ${hideVisually};
          }
        `};

  &:hover {
    background-color: ${({ theme }) => rgba(theme.NightBlue, 0.1)};
  }

  &:active {
    background-color: ${({ theme }) => rgba(theme.NightBlue, 0.15)};
  }

  .icon-checkmark {
    stroke-dasharray: 100;
    animation: ${animateCheckmark} 0.75s forwards;
  }
`;

const unfollowStyle = ({ theme }) => css`
  color: white;
  background: ${rgba(theme.CasperGrey, 0.5)};

  &:hover {
    background: ${rgba(theme.CasperGrey, 0.75)};
  }

  &:active {
    background: ${rgba(theme.CasperGrey, 1)};
  }
`;

export const followButtonStyles = () => css`
  appearance: none;
  text-transform: uppercase;
  font-size: ${modularScale(-2)};
  letter-spacing: 0.1em;
  padding: ${({ theme }) => `${theme.spacing.xsmall} ${theme.spacing.medium}`};
  transition: ${({ theme }) =>
    `all ${theme.transitionDuration} ${theme.timingFunction}`};
  border-radius: 0.2em;
  box-sizing: content-box;
  text-align: center;
  position: relative;
  white-space: nowrap;
  margin: 0;
  font-weight: 600;

  /* Disable for a false positive */
  /* stylelint-disable-next-line no-descending-specificity */
  svg {
    vertical-align: middle;
    margin-right: ${({ theme }) => theme.spacing.small};
    margin-top: -2px;
  }
`;

const FollowButton = styled.button`
  ${followButtonStyles}

  ${({ isFollowing, buttonType, hasError }) =>
    buttonType === 'unfollow'
      ? unfollowStyle
      : isFollowing || hasError
      ? toggleIsFollowing
      : toggleIsNotFollowing};
`;

const ButtonContainer = styled('div')`
  display: flex;
  position: relative;
  justify-content: center;

  &:hover p {
    opacity: 1;
    visibility: visible;
    transform: translate(-50%, 0);
  }

  @media screen and (min-width: 40em) {
    justify-content: flex-start;
  }
`;

const maxFollowingEntityCount = 100;

/** Following Entity toggle button that sets te following state of an Entity in Redux */
export const FollowEntityButton = ({
  isFollowing,
  followingEntityCount,
  hasTooltip,
  buttonType,
  isLoading,
  handleClick,
  hasError,
  isHovered,
  onMouseEnter,
  onMouseLeave,
}) => {
  const followText = isFollowing
    ? isHovered || buttonType === 'unfollow'
      ? 'Unfollow'
      : 'Following'
    : 'Follow';
  const buttonText =
    hasError && !isHovered && !isLoading ? 'try again' : followText;
  const emptySlots = maxFollowingEntityCount - followingEntityCount;

  const noEmptySlots = emptySlots <= 0;
  const isDisabled = (noEmptySlots && !isFollowing) || isLoading;

  let showTooltip = false;

  let tooltipContent = '';

  if (!isFollowing && hasTooltip) {
    if (noEmptySlots) {
      showTooltip = true;
      tooltipContent = `Unfortunately you reached your limit. You can only follow
      ${maxFollowingEntityCount} entities.`;
    } else if (emptySlots <= 5) {
      showTooltip = true;
      tooltipContent = `You only have ${emptySlots} more entities left that you can follow.`;
    }
  }

  return (
    <ButtonContainer>
      <TippyToolTip enabled={showTooltip} tooltipContent={tooltipContent}>
        <FollowButton
          isFollowing={isFollowing}
          isDisabled={isDisabled}
          buttonType={buttonType}
          isHovered={isHovered}
          onClick={() => {
            if (!isDisabled) {
              handleClick();

              if (buttonType !== 'unfollow' && onMouseLeave) {
                /** Remove the hover state */
                onMouseLeave();
              }
            }
          }}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
          hasError={hasError}
          data-gtag="follow-button"
        >
          {hasError ? (
            <IconTryAgain />
          ) : isFollowing ? (
            <>
              <IconUnfollow />
              {buttonType !== 'unfollow' && <IconCheckmark />}
            </>
          ) : null}
          {buttonText}
        </FollowButton>
      </TippyToolTip>
    </ButtonContainer>
  );
};

FollowEntityButton.defaultProps = {
  buttonType: 'toggle',
  isFollowing: false,
  followingEntityCount: 0,
  hasTooltip: false,
  isLoading: false,
  isHovered: false,
};

FollowEntityButton.propTypes = {
  /** Is this a toggle button or will you only be able to unfollow */
  buttonType: oneOf(['toggle', 'unfollow']),
  /** Is the entity currently followed by the user (only for buttonType toggle) */
  isFollowing: bool,
  /** The total number of entities that is currently being followed (only for buttonType toggle) */
  followingEntityCount: number,
  /** Should a tooltip display when you (almost) reached your maximum number of entities (only for buttonType toggle) */
  hasTooltip: bool,
  /** Is the button currently hovered? */
  isHovered: bool,
  /** Is the button currently in loading state? */
  isLoading: bool,
  /** Did the last Redux call error? */
  hasError: bool,
  /** The click handler */
  handleClick: func.isRequired,
  /** This sets the hovered state */
  onMouseEnter: func,
  /** This removes the hovered state */
  onMouseLeave: func,
};
